// Le schede di collegamento del market per Gmail e Pushover (tela, artboard 44). Sono solo campi:
// lo stato e l'invio stanno in AddTool, come per memo e i server MCP.
import { useState } from 'react';
import type { PushRecipient } from '../../lib/api';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Field, TextField } from '../../components/Field';
import { Icon } from '../../components/Icon';

/** L'indirizzo di ritorno di Google per questa plancia: da copiare nel client OAuth. */
export const googleRedirect = () => `${window.location.origin}/api/tools/google/callback`;

export type Credentials = { clientId: string; clientSecret: string };

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li style={{ display: 'flex', gap: 10 }}>
      <span
        aria-hidden="true"
        style={{
          flex: 'none',
          width: 20,
          height: 20,
          borderRadius: '50%',
          background: 'var(--accent-soft)',
          color: 'var(--accent)',
          font: '600 11px/20px var(--font-ui)',
          textAlign: 'center',
        }}
      >
        {n}
      </span>
      <span>{children}</span>
    </li>
  );
}

export function GmailForm({
  value,
  onChange,
  nameField,
}: {
  value: Credentials;
  onChange: (v: Credentials) => void;
  /** il campo «Nome», prima delle credenziali come nella tela */
  nameField: React.ReactNode;
}) {
  const [copied, setCopied] = useState(false);
  const back = googleRedirect();
  const copy = () =>
    void navigator.clipboard.writeText(back).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  return (
    <>
      <div className="t-small dim">
        Gmail si collega con OAuth di Google. Il client lo crei tu, una volta sola: vale per tutti i
        tuoi account.
      </div>
      <ol
        className="t-small"
        style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 8 }}
      >
        <Step n={1}>
          Crea un progetto su{' '}
          <a
            href="https://console.cloud.google.com/apis/library/gmail.googleapis.com"
            target="_blank"
            rel="noreferrer"
          >
            console.cloud.google.com
          </a>{' '}
          e abilita la <b>Gmail API</b>.
        </Step>
        <Step n={2}>
          Schermata di consenso: tipo <b>Esterno</b>. L'app può restare in «Test»: in{' '}
          <b>Pubblico › Utenti di test</b> aggiungi gli indirizzi Gmail che collegherai, altrimenti
          Google blocca l'accesso.
        </Step>
        <Step n={3}>
          Credenziali › Crea ID client OAuth › <b>Applicazione web</b>, con questo URI di
          reindirizzamento:
        </Step>
      </ol>
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          border: '1px solid var(--border)',
          background: 'var(--panel-2)',
          borderRadius: 'var(--r-md)',
          padding: '6px 6px 6px 10px',
        }}
      >
        <code
          className="t-mono-xs"
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {back}
        </code>
        <Button size="sm" variant="secondary" icon={copied ? 'check' : 'copy'} onClick={copy}>
          {copied ? 'Copiato' : 'Copia'}
        </Button>
      </div>
      <div className="t-small dim">
        <b>Al collegamento</b>, nella schermata del consenso <b>spunta la casella della posta</b>{' '}
        («Leggere, scrivere, inviare ed eliminare definitivamente le email di Gmail»): Google la
        lascia vuota, e senza PopTown non riceve nessuno strumento. Google mostrerà anche «app non
        verificata»: per uso tuo va bene, scegli Avanzate › Vai a PopTown.
      </div>
      <Banner kind="warn">
        Con l'app in «Test» l'accesso scade ogni 7 giorni: PopTown lo segna sulla scheda e in
        Notifiche, e basta «Ricollega». Pubblicata «In produzione», non scade più.
      </Banner>
      {nameField}
      <Field label="Client ID">
        <TextField
          value={value.clientId}
          onChange={(clientId) => onChange({ ...value, clientId })}
          placeholder="….apps.googleusercontent.com"
          mono
        />
      </Field>
      <Field
        label="Client secret"
        hint="Restano cifrati sul server di PopTown. L'accesso chiesto è completo: quali strumenti usano i Pops lo scegli dopo, uno per uno."
      >
        <TextField
          value={value.clientSecret}
          onChange={(clientSecret) => onChange({ ...value, clientSecret })}
          type="password"
          mono
        />
      </Field>
    </>
  );
}

export function PushoverHelp() {
  return (
    <div className="t-small dim">
      Crea un'applicazione su{' '}
      <a href="https://pushover.net/apps/build" target="_blank" rel="noreferrer">
        pushover.net/apps/build
      </a>{' '}
      e incolla il suo API Token. Le chiavi utente stanno nella tua pagina di Pushover.
    </div>
  );
}

const cell: React.CSSProperties = {
  minWidth: 0,
  height: 34,
  padding: '0 10px',
  borderRadius: 'var(--r-sm)',
  border: '1px solid var(--border-strong)',
  background: 'var(--panel)',
  color: 'var(--text)',
  font: '400 14px/20px var(--font-ui)',
};

/** I destinatari Pushover: nome e chiave utente o di gruppo, quanti se ne vuole (fino a 20). */
export function RecipientsEditor({
  value,
  onChange,
}: {
  value: PushRecipient[];
  onChange: (v: PushRecipient[]) => void;
}) {
  const set = (i: number, part: Partial<PushRecipient>) =>
    onChange(value.map((r, j) => (j === i ? { ...r, ...part } : r)));
  return (
    <fieldset style={{ border: 'none', padding: 0, margin: 0, display: 'grid', gap: 8 }}>
      <legend className="t-body" style={{ fontWeight: 600, padding: 0, marginBottom: 4 }}>
        Destinatari
      </legend>
      <div className="t-caption dim">
        Il pop sceglie per nome; una chiave fuori elenco non si usa mai. PopTown le verifica con
        Pushover.
      </div>
      {value.map((r, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 32px', gap: 6 }}>
          <input
            aria-label={`Nome del destinatario ${i + 1}`}
            value={r.name}
            onChange={(e) => set(i, { name: e.target.value })}
            placeholder="luca"
            style={cell}
          />
          <input
            aria-label={`Chiave utente o di gruppo ${i + 1}`}
            value={r.key}
            onChange={(e) => set(i, { key: e.target.value })}
            placeholder="chiave utente o di gruppo"
            style={{ ...cell, fontFamily: 'var(--font-mono)' }}
          />
          <button
            type="button"
            aria-label={`Togli ${r.name || `il destinatario ${i + 1}`}`}
            onClick={() => onChange(value.filter((_, j) => j !== i))}
            style={{
              width: 32,
              height: 34,
              border: 'none',
              background: 'none',
              color: 'var(--dim)',
              display: 'grid',
              placeItems: 'center',
              cursor: 'pointer',
            }}
          >
            <Icon name="x" size={16} />
          </button>
        </div>
      ))}
      {value.length < 20 && (
        <div>
          <Button
            size="sm"
            variant="ghost"
            icon="plus"
            onClick={() => onChange([...value, { name: '', key: '' }])}
          >
            Aggiungi destinatario
          </Button>
        </div>
      )}
    </fieldset>
  );
}
